import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService, AuthenticationService } from '@alfresco/adf-core';
import { catchError, map, tap } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import {
  EbenPagination,
  EbenProcessInstance,
  EbenProcessInstanceQueryRepresentation,
  EbenTaskDetail,
  EbenTaskQueryRepresentation,
} from '@alf-nx-workspace/eben/interfaces';
import { ResultListDataRepresentationLightUserRepresentation } from '@alfresco/js-api/src/api/activiti-rest-api/model/resultListDataRepresentationLightUserRepresentation';
import { AlfrescoApiConfig } from '@alfresco/js-api/src/alfrescoApiConfig';
import { QueryError } from './error.service';
import { Organisation } from '@alf-nx-workspace/shared/interfaces';
import { RelatedContentRepresentation } from '@alfresco/js-api';

@Injectable({
  providedIn: 'root',
})
export class BpmService {
  readonly alfrescoApiService: AlfrescoApiService = inject(AlfrescoApiService);
  readonly authenticationService: AuthenticationService = inject(AuthenticationService);

  private readonly BOKU_USER_ROOT = '/activiti-app/api/enterprise/boku-flow/users';
  private readonly BOKU_PROCESS_INSTANCE_ROOT = '/activiti-app/api/enterprise/boku-flow/process-instances';
  private readonly BOKU_TASK_ROOT = '/activiti-app/api/enterprise/boku-flow/tasks';
  private readonly BOKU_CONTENT_ROOT = '/activiti-app/api/enterprise/boku-flow/content';
  private readonly BOKU_MASTERARBEITSANMELDUNG_ROOT = '/activiti-app/api/enterprise/boku-flow/masterarbeitsanmeldung';
  private readonly BOKU_EMAIL_ROOT = '/activiti-app/api/enterprise/boku-flow/email';
  private readonly CUSTOM_USER_ROOT = '/activiti-app/api/enterprise/custom/users';
  private readonly ORGEH_ROOT = '/activiti-app/api/enterprise/boku-flow/orgeh';

  private callApi<T>(path: string, method: string, bodyParam?: unknown, pathParams?: unknown, queryParams?: unknown, headerParams: unknown = null, formParams: unknown = null, contentTypes = ['application/json'], accepts = ['application/json']): Observable<T> {
    const apiConfig: AlfrescoApiConfig = this.alfrescoApiService.getInstance().config;
    const url = `${apiConfig.hostBpm}${path}`;

    return from(
      this.alfrescoApiService.getInstance().oauth2Auth.callCustomApi(
        url, method, pathParams, queryParams, headerParams, formParams, bodyParam, contentTypes, accepts,
      )
    )
      .pipe(
      tap(() => {
        //throw new Error('ERROR')
      })
    );
  }

  public getUser(userId: number): Observable<ResultListDataRepresentationLightUserRepresentation> {
    const path = `${this.BOKU_USER_ROOT}/{userId}`;
    return this.callApi<ResultListDataRepresentationLightUserRepresentation>(path, 'GET', null, { userId }).pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'USER', { userId })))
    );
  }

  /**
   * Query users with a filter and optional groupId restriction. Uses custom endpoint.
   * Filter will be applied to firstname, lastname, fullname, username, email
   * Returns only active users
   * @param filter can be empty string to find all available users
   * @param groupId can be empty string to search without group restrictions
   * @param representedBy  takes an ext.Id (BOKU UUID) and limits the search to users for which this user is a deputy. Can be empty string or omitted
   */
  public queryUsers(filter: string, groupId?: string, representedBy?: string): Observable<ResultListDataRepresentationLightUserRepresentation> {
    const path = `${this.CUSTOM_USER_ROOT}/query`;
    const queryParams = {
      filter: filter || '',
      groupId,
      representedBy,
    };
    return this.callApi<ResultListDataRepresentationLightUserRepresentation>(path, 'GET', null, null, queryParams).pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'USER', queryParams)))
    );
  }

  public queryAffectedUsers(filter: string, groupId?: string, representedBy?: string): Observable<ResultListDataRepresentationLightUserRepresentation> {
    const path = `${this.CUSTOM_USER_ROOT}/affected-user/query`;
    const queryParams = {
      filter: filter || '',
      groupId,
      representedBy
    };
    return this.callApi<ResultListDataRepresentationLightUserRepresentation>(path, 'GET', null, null, queryParams).pipe(
      catchError(error => throwError(QueryError.createFromError(error, 'USER', queryParams)))
    );
  }


  public loadMyWorkflows(query: EbenProcessInstanceQueryRepresentation): Observable<EbenPagination<EbenProcessInstance>> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/my-workflows`;
    return this.callApi<EbenPagination<EbenProcessInstance>>(path, 'POST', query).pipe(
      map((response) => {
        if (response.data) {
          response.data = this.sortProcessInstancesTaskList(response.data);
        }
        return response;
      }),
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', query)))
    );
  }

  public loadMyWorkflowsNames(): Observable<string[]> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/my-workflows/definition-keys`;
    return this.callApi<string[]>(path, 'GET').pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', path)))
    );
  }

  public loadOrgEhWorkflows(
    query: EbenProcessInstanceQueryRepresentation
  ): Observable<EbenPagination<EbenProcessInstance>> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/orgeh-workflows`;
    return this.callApi<EbenPagination<EbenProcessInstance>>(path, 'POST', query).pipe(
      map((response) => {
        if (response.data) {
          response.data = this.sortProcessInstancesTaskList(response.data);
        }
        return response;
      }),
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', query)))
    );
  }

  public loadOrgEhWorkflowsNames(): Observable<string[]> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/orgeh-workflows/definition-keys`;
    return this.callApi<string[]>(path, 'GET').pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', path)))
    );
  }

  public loadAdminWorkflows(
    query: EbenProcessInstanceQueryRepresentation
  ): Observable<EbenPagination<EbenProcessInstance>> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/admin-workflows`;
    return this.callApi<EbenPagination<EbenProcessInstance>>(path, 'POST', query).pipe(
      map((response) => {
        if (response.data) {
          response.data = this.sortProcessInstancesTaskList(response.data);
        }
        return response;
      }),
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', query)))
    );
  }

  public loadAdminWorkflowsNames(): Observable<string[]> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/query/admin-workflows/definition-keys`;
    return this.callApi<string[]>(path, 'GET').pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESSES', path)))
    );
  }

  public getProcessInstanceById(processInstanceId: string): Observable<EbenProcessInstance> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/{processInstanceId}`;
    return this.callApi<EbenProcessInstance>(path, 'GET', null, { processInstanceId }).pipe(
      map((processInstance: EbenProcessInstance) => {
        if (processInstance.taskRepresentations) {
          processInstance.taskRepresentations = this.sortTasks(processInstance.taskRepresentations);
        }
        return processInstance;
      }),
      catchError((error) => throwError(QueryError.createFromError(error, 'PROCESS', processInstanceId)))
    );
  }

  public cancelProcessInstanceAsUser(processInstanceId: string, enforceWhitelistedProcessDefinition: boolean = false): Observable<void> {
    return this.cancelProcessInstance(processInstanceId, "WORKFLOW.CANCELLED.BY_INITIATOR");
  }

  public cancelProcessInstanceAsAdmin(processInstanceId: string, hasBeenCancelledComment: string): Observable<void> {
    return this.cancelProcessInstance(processInstanceId, hasBeenCancelledComment);
  }

  public cancelProcessInstance(processInstanceId: string, hasBeenCancelledComment: string): Observable<void> {
    const path = `${this.BOKU_PROCESS_INSTANCE_ROOT}/{processInstanceId}`;
    return this.callApi<void>(path, 'DELETE', null, {processInstanceId}, {hasBeenCancelledComment}).pipe(
      catchError(error => throwError(QueryError.createFromError(error, 'PROCESS-CANCEL', processInstanceId)))
    );
  }

  public queryTasks(query: EbenTaskQueryRepresentation): Observable<EbenPagination<EbenTaskDetail>> {
    const path = `${this.BOKU_TASK_ROOT}/query`;
    return this.callApi<EbenPagination<EbenTaskDetail>>(path, 'POST', query).pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'TASKS', query)))
    );
  }

  public getTaskById(taskId: string): Observable<EbenTaskDetail> {
    const path = `${this.BOKU_TASK_ROOT}/{taskId}`;
    return this.callApi<EbenTaskDetail>(path, 'GET', null, { taskId }).pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'TASK', taskId)))
    );
  }

  public getTaskNames(): Observable<{ processDefinitionKey: string; taskNames: string[] }[]> {
    const path = `${this.BOKU_TASK_ROOT}/task-names`;
    return this.callApi<{ processDefinitionKey: string; taskNames: string[] }[]>(path, 'GET').pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'TASKS', path)))
    );
  }

  public loadOrganisationen(): Observable<Organisation[]> {
    const path = `${this.ORGEH_ROOT}/organisationsBySyncKey`;
    return this.callApi<Organisation[]>(path, 'GET').pipe(
      catchError((error) => throwError(QueryError.createFromError(error, 'ORGANISATION', path)))
    );
  }

  public createTemporaryRawRelatedContent(file, validPlaceholders, validDocumentTypes, rejectEncryptedPdf?): Observable<RelatedContentRepresentation> {
    const path = `${this.BOKU_CONTENT_ROOT}/raw`;
    const params = {file, validPlaceholders, validDocumentTypes, rejectEncryptedPdf};
    return this.callApi<RelatedContentRepresentation>(path, 'POST', null, null, null, null, params, ['multipart/form-data']).pipe(
      catchError(error => throwError(QueryError.createFromError(error, 'CONTENT', path)))
    );
  }

  public createTemporaryRelatedContent(relatedContent: RelatedContentRepresentation, validDocumentTypes: string[], validPlaceholders: string[]): Observable<RelatedContentRepresentation> {
    const path = `${this.BOKU_CONTENT_ROOT}`;
    const request = {relatedContent, validPlaceholders, validDocumentTypes};
    return this.callApi<RelatedContentRepresentation>(path, 'POST', request).pipe(
      catchError(error => throwError(QueryError.createFromError(error, 'CONTENT', path)))
    );
  }
  // @deprecated
  getLatestProcessDefinitionId(appName: string, processDefinitionKey: string) {}

  private sortProcessInstancesTaskList(ebenProcessInstances: EbenProcessInstance[]): EbenProcessInstance[] {
    return ebenProcessInstances.map((processInstance) => {
      if (processInstance.taskRepresentations) {
        processInstance.taskRepresentations = this.sortTasks(processInstance.taskRepresentations);
      }
      return processInstance;
    });
  }

  private sortTasks(taskRepresentations: EbenTaskDetail[]): EbenTaskDetail[] {
    const currentUserId = this.authenticationService.getBpmUsername();
    return taskRepresentations.sort((taskA, taskB) => {
      const priorityA = this.getTaskPriority(taskA, currentUserId);
      const priorityB = this.getTaskPriority(taskB, currentUserId);
      return priorityA - priorityB;
    });
  }

  private getTaskPriority(task: EbenTaskDetail, currentUserId: string): number {
    if (task.assignee?.externalId === currentUserId) {
      return 1;
    }
    if (task.memberOfCandidateGroup) {
      return 2;
    }
    return 3;
  }

  /**
   * process masterarbeitsanmeldung
   * Prüft ob der User schon einen Workflow für dieses Studium gestartet hat
   * @param studium
   */
  public checkUserProcessAlreadyExists(studium: string): Observable<boolean> {
    const path = `${this.BOKU_MASTERARBEITSANMELDUNG_ROOT}/checkUserProcessAlreadyExists/{studium}`;
    return this.callApi<boolean>(path, 'GET', null, { studium });
  }

  public validateEmails(emails: string): Observable<string[]> {
    const path = `${this.BOKU_EMAIL_ROOT}/validate`;
    return this.callApi<string[]>(path, 'POST', emails);
  }
}
